import React, { useState, useEffect, useContext } from "react";
import { Box, Text } from "grommet";
import { loadStripe } from "@stripe/stripe-js";
import Modal from "./Modal";
import Loading from "./Loading";
import Button from "./Button";
import { handleErrors } from "../utils/errors";
import { formatCurrency } from "../utils/numbers";
import { NotificationsContext } from "./NotificationsProvider";
import { faCircleCheck as IconSuccess } from "@fortawesome/pro-light-svg-icons";
import FontAwesomeIcon from "./FontAwesomeIcon";

const ERROR_MESSAGES = {
    INVALID_LINK: "This link is no longer valid",
    ALREADY_LINKED: "Bank account already linked",
    SESSION_EXPIRED: "Bank linking session has expired. Please request a new link.",
    GENERIC: "An error occurred. Please try again later.",
};

// Refund Details Component
const RefundDetails = ({ data }) => (
    <Box border={{ color: "border-strong", side: "all" }} round="xsmall" pad="medium" gap="small">
        <Box direction="row" justify="between" align="center">
            <Text color="text-weak">Refund Amount</Text>
            <Text weight="500">{formatCurrency(data?.amount, data?.currency)}</Text>
        </Box>
        <Box direction="row" justify="between" align="center">
            <Text color="text-weak">From</Text>
            <Text weight="500">{data?.company_name}</Text>
        </Box>
        <Box direction="row" justify="between" align="center">
            <Text color="text-weak">To</Text>
            <Text weight="500">{data?.customer_name}</Text>
        </Box>
    </Box>
);

// Main Modal Component
const RefundBankLinkModal = ({ customerUuid, token, onClose, onSuccess }) => {
    const notificationCtx = useContext(NotificationsContext);

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLinking, setIsLinking] = useState(false);

    useEffect(() => {
        if (!customerUuid || !token) return;

        fetch(`/api/checkout/${customerUuid}/refund-bank-link/${token}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        })
            .then(handleErrors)
            .then((responseData) => {
                setData(responseData);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                if (err.status === 404) {
                    setError(ERROR_MESSAGES.INVALID_LINK);
                } else if (err.status === 422) {
                    const message = err.message || "";
                    if (message.toLowerCase().includes("not required") || message.toLowerCase().includes("already linked")) {
                        setError(ERROR_MESSAGES.ALREADY_LINKED);
                    } else if (message.toLowerCase().includes("session")) {
                        setError(ERROR_MESSAGES.SESSION_EXPIRED);
                    } else {
                        setError(message || ERROR_MESSAGES.GENERIC);
                    }
                } else {
                    setError(ERROR_MESSAGES.GENERIC);
                    notificationCtx?.showNotification?.({
                        message: err.message || ERROR_MESSAGES.GENERIC,
                        type: "error",
                    });
                }
            });
    }, [customerUuid, token]);

    const handleLinkBankAccount = async () => {
        if (!data?.client_secret || !data?.publishable_key) {
            setError(ERROR_MESSAGES.GENERIC);
            return;
        }

        setIsLinking(true);

        try {
            const stripe = await loadStripe(data.publishable_key);
            if (!stripe) {
                setError("Failed to initialize Stripe");
                setIsLinking(false);
                return;
            }

            // Step 1: Collect bank account (opens Financial Connections modal)
            const { setupIntent, error: collectError } = await stripe.collectBankAccountForSetup({
                clientSecret: data.client_secret,
                params: {
                    payment_method_type: "us_bank_account",
                    payment_method_data: {
                        billing_details: {
                            name: data.customer_name,
                        },
                    },
                },
            });

            if (collectError) {
                setIsLinking(false);
                const errorMessage = collectError.message || "";

                if (errorMessage.toLowerCase().includes("expired")) {
                    setError(ERROR_MESSAGES.SESSION_EXPIRED);
                    return;
                }

                if (collectError.type === "validation_error") {
                    setError(collectError.message);
                } else {
                    notificationCtx?.showNotification?.({
                        message: collectError.message || ERROR_MESSAGES.GENERIC,
                        type: "error",
                    });
                }
                return;
            }

            // Step 2: Confirm the SetupIntent to create the PaymentMethod
            if (setupIntent?.status === "requires_confirmation") {
                const { setupIntent: confirmedIntent, error: confirmError } =
                    await stripe.confirmUsBankAccountSetup(data.client_secret);

                if (confirmError) {
                    setIsLinking(false);
                    const errorMessage = confirmError.message || "";

                    if (errorMessage.toLowerCase().includes("expired")) {
                        setError(ERROR_MESSAGES.SESSION_EXPIRED);
                        return;
                    }

                    notificationCtx?.showNotification?.({
                        message: confirmError.message || ERROR_MESSAGES.GENERIC,
                        type: "error",
                    });
                    return;
                }

                if (confirmedIntent?.status === "succeeded" || confirmedIntent?.status === "processing") {
                    setIsSuccess(true);
                    onSuccess?.();
                }
            } else if (setupIntent?.status === "succeeded") {
                // Already succeeded (shouldn't happen but handle it)
                setIsSuccess(true);
                onSuccess?.();
            }
        } catch (err) {
            setError(err.message || ERROR_MESSAGES.GENERIC);
        } finally {
            setIsLinking(false);
        }
    };

    const getTitle = () => {
        if (isSuccess) return "Success";
        if (error) return "Bank connection unsuccessful";
        return "Link bank account";
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box align="center" justify="center" pad="medium" height={{ min: "small" }}>
                    <Loading />
                </Box>
            );
        }

        if (error) {
            return (
                <Box gap="medium">
                    <Text textAlign="center">{error}</Text>
                    {error === ERROR_MESSAGES.ALREADY_LINKED && (
                        <Text textAlign="center" color="text-weak" size="small">
                            Your bank account has already been linked for this refund.
                        </Text>
                    )}
                </Box>
            );
        }

        if (isSuccess) {
            return (
                <Box align="center" gap="medium">
                    <Box background="status-ok" round="full" pad="medium">
                        <FontAwesomeIcon SelectedIcon={IconSuccess} size="xlarge" color="white" />
                    </Box>
                    <Text textAlign="center">
                        Your bank account has been successfully linked. Your refund of{" "}
                        <Text weight="bold">{formatCurrency(data?.amount, data?.currency)}</Text>{" "}
                        will be processed shortly.
                    </Text>
                </Box>
            );
        }

        return (
            <Box gap="medium">
                <RefundDetails data={data} />
                <Text size="small" color="text-weak" textAlign="center">
                    Link your bank account to receive your refund. Your account information is securely handled by Stripe.
                </Text>
                <Button
                    primary
                    fill="horizontal"
                    label={isLinking ? "Linking..." : "Link Bank Account"}
                    onClick={handleLinkBankAccount}
                    disabled={isLinking}
                />
            </Box>
        );
    };

    return (
        <Modal
            onClose={onClose}
            title={getTitle()}
            footerProps={{ pad: { top: 'xsmall', bottom: 'small' } }}
            containerProps={{ width: '500px' }}
            hideDeepLink={true}
            hideCancelBtn
            {...(isSuccess || error ? {
                onSubmit: onClose,
                ctaText: isSuccess ? "Done" : "Close",
            } : {})}
        >
            {renderContent()}
        </Modal>
    );
};

export default RefundBankLinkModal;
